/**
 * Adds collapse/expand functionality to code blocks marked with <!-- collapse --> markers.
 * Collapsed blocks show a configurable number of lines with an expand toggle.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

const DEFAULT_VISIBLE_LINES = 15;
// Tolerates whitespace around the optional `: TITLE` and `| N` parts, so
// `<!-- collapse -->`, `<!-- collapse | 20 -->` and `<!-- collapse: T | 5 -->`
// all match (mirrors COLLAPSE_LINE_RE in editor/collapse-scan.ts).
const COLLAPSE_PATTERN = /^\s*collapse\s*(?::\s*([^|]*?))?\s*(?:\|\s*(\d+)\s*)?$/;

// Numeric nodeType constants (the `Node` global isn't available in unit tests)
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const COMMENT_NODE = 8;

/**
 * Find the next code block (pre element or code-block-wrapper) after a comment
 * node, skipping whitespace text nodes AND other comment nodes. Leedoc markers
 * are all HTML comments (`<!-- tangle -->`, `<!-- embed -->`, …) that survive
 * into the rendered DOM, and their order relative to `<!-- collapse -->` must
 * not matter — a collapse marker followed by a tangle marker still collapses
 * the block after both.
 */
export function findCodeBlockAfterComment(comment: Comment): HTMLElement | null {
  let sibling = comment.nextSibling;
  while (sibling) {
    if (sibling.nodeType === ELEMENT_NODE) {
      const el = sibling as HTMLElement;
      // Check for pre directly or wrapped in code-block-wrapper
      if (el.tagName === 'PRE') return el;
      if (el.classList.contains('code-block-wrapper')) return el;
      // Any other element ends the search
      return null;
    }
    // Skip whitespace text nodes and other marker comments between the
    // collapse marker and the code block
    if (
      sibling.nodeType === COMMENT_NODE ||
      (sibling.nodeType === TEXT_NODE && sibling.textContent?.trim() === '')
    ) {
      sibling = sibling.nextSibling;
      continue;
    }
    return null;
  }
  return null;
}

export class CollapseEnricher extends BaseEnricher {
  constructor(config: EnricherConfig = {}) {
    super({ priority: 85, ...config });
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    // Collect comment nodes matching collapse pattern
    const collapseDirectives = this.findCollapseComments(container);

    for (const { comment, title, visibleLines } of collapseDirectives) {
      const target = findCodeBlockAfterComment(comment);
      if (!target) {
        comment.remove();
        continue;
      }

      this.wrapWithCollapse(target, title, visibleLines);
      comment.remove();
    }
  }

  /**
   * Walk DOM to find HTML comment nodes matching collapse pattern.
   */
  private findCollapseComments(container: HTMLElement): Array<{
    comment: Comment;
    title: string;
    visibleLines: number;
  }> {
    const results: Array<{ comment: Comment; title: string; visibleLines: number }> = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);

    let node: Comment | null;
    while ((node = walker.nextNode() as Comment | null)) {
      const match = node.textContent?.match(COLLAPSE_PATTERN);
      if (match) {
        const title = (match[1] ?? '').trim();
        const visibleLines = match[2] ? parseInt(match[2], 10) : DEFAULT_VISIBLE_LINES;
        results.push({ comment: node, title, visibleLines });
      }
    }

    return results;
  }

  /**
   * Wrap a code block element with collapse UI.
   */
  private wrapWithCollapse(target: HTMLElement, title: string, visibleLines: number): void {
    // Get the pre element (may be inside a code-block-wrapper)
    const pre = target.tagName === 'PRE' ? target : target.querySelector('pre');
    if (!pre) return;

    const code = pre.querySelector('code');
    if (!code) return;

    // Count total lines
    const text = code.textContent ?? '';
    const trimmed = text.endsWith('\n') ? text.slice(0, -1) : text;
    const totalLines = trimmed.split('\n').length;

    // Don't collapse if content is shorter than visible lines
    if (totalLines <= visibleLines) {
      // Still show the header if there's a title
      if (title) {
        this.addHeaderOnly(target, title, totalLines);
      }
      return;
    }

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'code-collapse-wrapper collapsed';
    wrapper.dataset.visibleLines = String(visibleLines);

    // Create header
    const header = this.createHeader(title, totalLines);

    // Create fade overlay
    const fade = document.createElement('div');
    fade.className = 'code-collapse-fade';

    // Create toggle button
    const toggle = this.createToggleButton(totalLines, visibleLines);

    // Set collapsed height on the pre element
    const preEl = pre as HTMLElement;
    // line-height is 1.4 * 13px = 18.2px, plus padding
    const lineHeight = 18.2;
    const padding = 12; // top padding of pre
    preEl.style.maxHeight = `${(visibleLines * lineHeight) + padding}px`;
    preEl.style.overflow = 'hidden';

    // Insert wrapper into DOM
    target.parentNode!.insertBefore(wrapper, target);
    wrapper.appendChild(header);
    wrapper.appendChild(target);
    wrapper.appendChild(fade);
    wrapper.appendChild(toggle);

    // Toggle handler
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isCollapsed = wrapper.classList.contains('collapsed');

      if (isCollapsed) {
        wrapper.classList.remove('collapsed');
        preEl.style.maxHeight = '';
        preEl.style.overflow = '';
        toggle.textContent = 'Show less';
      } else {
        wrapper.classList.add('collapsed');
        preEl.style.maxHeight = `${(visibleLines * lineHeight) + padding}px`;
        preEl.style.overflow = 'hidden';
        toggle.textContent = `Show all ${totalLines} lines`;
      }
    });
  }

  /**
   * Add header only (no collapse) when content is shorter than visible lines.
   */
  private addHeaderOnly(target: HTMLElement, title: string, totalLines: number): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'code-collapse-wrapper';

    const header = this.createHeader(title, totalLines);

    target.parentNode!.insertBefore(wrapper, target);
    wrapper.appendChild(header);
    wrapper.appendChild(target);
  }

  /**
   * Create the header bar with title and line count.
   */
  private createHeader(title: string, totalLines: number): HTMLDivElement {
    const header = document.createElement('div');
    header.className = 'code-collapse-header';

    if (title) {
      const titleSpan = document.createElement('span');
      titleSpan.className = 'code-collapse-title';
      titleSpan.textContent = title;
      titleSpan.title = 'Click to copy filename';
      titleSpan.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(title);
          titleSpan.classList.add('copied');
          setTimeout(() => {
            titleSpan.classList.remove('copied');
          }, 800);
        } catch (err) {
          console.error('Failed to copy title:', err);
        }
      });
      header.appendChild(titleSpan);
    }

    const badge = document.createElement('span');
    badge.className = 'code-collapse-badge';
    badge.textContent = `${totalLines} lines`;
    header.appendChild(badge);

    return header;
  }

  /**
   * Create the expand/collapse toggle button.
   */
  private createToggleButton(totalLines: number, _visibleLines: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'code-collapse-toggle';
    btn.textContent = `Show all ${totalLines} lines`;
    return btn;
  }
}
